import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PartyPopper, Plus, Settings2, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import { EventDetailsEditor } from "./opendays/EventDetailsEditor";
import { TicketHolders } from "./opendays/TicketHolders";

const nz = (n) => `NZ$${Number(n || 0).toFixed(2)}`;

function saleState(e) {
  const s = e.stats || {};
  if (e.sold_out || s.auto_sold_out) return { label: e.sold_out ? "Sold out (forced)" : "Sold out", cls: "bg-[#FBF0F0] text-[#A33030]" };
  if (e.tickets_open && (e.ticket_types || []).length) return { label: "On sale", cls: "bg-[#E4F0E8] text-[#2F6B4F]" };
  return { label: "Not on sale", cls: "bg-[#F3EBDD] text-[#7A6A55]" };
}

function Stat({ label, value, tone = "", testid }) {
  return <div className="bg-white border border-[#E8DFCB] rounded p-3" data-testid={testid}><div className="text-[9px] uppercase tracking-[0.25em] text-[#7A6A55]">{label}</div><div className={`font-serif text-2xl mt-0.5 ${tone}`}>{value}</div></div>;
}

function StatsStrip({ e }) {
  const s = e.stats || {};
  const cap = s.capacity || 0;
  const pct = cap ? Math.min(100, Math.round(((s.sold || 0) / cap) * 100)) : 0;
  const st = saleState(e);
  return (
    <div className="space-y-2" data-testid="event-stats">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] ${st.cls}`} data-testid="event-sale-state">{st.label}</span>
        {!e.visible && <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] bg-[#F3EBDD] text-[#7A6A55]">Hidden from public</span>}
        <span className="text-xs text-[#7A6A55]">{e.date || "No date yet"}{e.times ? ` · ${e.times}` : ""}</span>
      </div>
      <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Capacity" value={cap || "∞"} testid="stat-capacity" />
        <Stat label="Sold" value={s.sold || 0} tone="text-[#2F6B4F]" testid="stat-sold" />
        <Stat label="Held (in checkout)" value={s.held || 0} tone="text-[#8B4A0B]" testid="stat-held" />
        <Stat label="Remaining" value={cap ? s.remaining : "∞"} tone={cap && s.remaining <= 20 ? "text-[#A33030]" : ""} testid="stat-remaining" />
        <Stat label="Revenue" value={nz(s.revenue)} testid="stat-revenue" />
        <Stat label="Checked in" value={`${s.checked_in || 0}/${s.sold || 0}`} testid="stat-checked-in" />
      </div>
      {cap > 0 && <div className="h-2 rounded-full bg-[#F3EBDD] overflow-hidden" title={`${pct}% sold`}><div className="h-full bg-[#3E1F46]" style={{ width: `${pct}%` }} /></div>}
    </div>
  );
}

export default function AdminOpenDays() {
  const [events, setEvents] = useState(null);
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("tickets");
  const load = useCallback(async () => {
    const r = await api.get("/admin/open-days/overview");
    setEvents(r.data);
    setSel((cur) => cur || r.data[0]?.id || null);
  }, []);
  useEffect(() => { load().catch(() => setEvents([])); }, [load]);
  const e = (events || []).find((x) => x.id === sel);

  const create = async () => {
    const title = window.prompt("Name of the new Open Day / event?");
    if (!title?.trim()) return;
    try { const r = await api.post("/admin/visit/open-days", { title: title.trim(), sort_order: (events?.length || 0) + 1 }); await load(); setSel(r.data.id); setTab("details"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't create event."); }
  };

  return (
    <div className="space-y-5" data-testid="admin-open-days">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.42em] uppercase font-semibold text-[#6E1E3A]">✦ Heritage Open Days</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#3C2A1E] mt-1">Open Days & tickets</h1>
          <p className="text-sm italic text-[#7A6A55] mt-1">Set ticket types and capacity, watch sales count down, run the gate on the day. Promo codes and the giveaway live in <Link to="/admin/visit" className="underline text-[#6E1E3A]">Visit Heritage</Link>.</p>
        </div>
        <button type="button" onClick={create} className="inline-flex items-center gap-2 h-10 px-4 rounded border text-xs font-semibold uppercase tracking-[0.2em]" style={{ borderColor: "#C9A268", color: "#3E1F46" }} data-testid="open-day-create"><Plus className="w-4 h-4" /> New event</button>
      </header>

      {events === null ? <Loader2 className="w-5 h-5 animate-spin text-[#6E1E3A]" /> : (
        <>
          <nav className="flex flex-wrap gap-2" data-testid="open-day-event-tabs">
            {events.map((ev) => {
              const st = saleState(ev);
              return (
                <button key={ev.id} type="button" onClick={() => setSel(ev.id)} data-testid={`open-day-tab-${ev.slug}`}
                  className={`text-left px-3 py-2 rounded-xl border text-xs ${sel === ev.id ? "bg-[#3E1F46] text-white border-[#3E1F46]" : "bg-white border-[#E8DFCB] text-[#3C2A1E]"}`}>
                  <div className="font-semibold flex items-center gap-1.5"><PartyPopper className="w-3.5 h-3.5" /> {ev.title}</div>
                  <div className={`text-[10px] mt-0.5 ${sel === ev.id ? "opacity-80" : "text-[#7A6A55]"}`}>{ev.stats?.sold || 0}{ev.stats?.capacity ? `/${ev.stats.capacity}` : ""} sold · <span className={sel === ev.id ? "" : st.cls.split(" ")[1]}>{st.label}</span></div>
                </button>
              );
            })}
          </nav>

          {e && (
            <>
              <StatsStrip e={e} />
              <div className="flex gap-2 border-b border-[#E8DFCB] pb-2">
                {[{ id: "tickets", label: "Ticket holders & gate", Icon: Users }, { id: "details", label: "Event, capacity & ticket types", Icon: Settings2 }].map(({ id, label, Icon }) => (
                  <button key={id} type="button" onClick={() => setTab(id)} data-testid={`open-day-subtab-${id}`}
                    className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold uppercase tracking-[0.15em] ${tab === id ? "bg-[#3E1F46] text-white" : "bg-white border border-[#E8DFCB] text-[#5A4565]"}`}><Icon className="w-3.5 h-3.5" /> {label}</button>
                ))}
              </div>
              {tab === "details" && <EventDetailsEditor key={e.id + e.updated_at} e={e} reload={load} />}
              {tab === "tickets" && <TicketHolders key={e.id} event={e} refreshStats={load} />}
            </>
          )}
        </>
      )}
    </div>
  );
}
